import React, { useState, useEffect } from 'react';
import {
    ComposedChart, BarChart, Bar, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Legend
} from 'recharts';
import { Sun, Moon, TrendingUp, Activity, BarChart2 } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';

const API_CONFIG = {
    BASE_URL: 'https://api.kkbconsulting.com',
    ENDPOINTS: {
        PORTFOLIO_HISTORY: (days) => `/api/portfolio/history?days=${days}`
    }
};

const C = {
    bgDark: '#0B5867',   // Deep Teal
    bgMid: '#2B6873',   // Mid Teal
    bgLight: '#9FBCC0',   // Mist
    accent: '#FF8C7A',   // Coral
    success: '#4ade80',   // Green
    danger: '#ef4444',   // Red
    white: '#FFFFFF',
    textSecondary: '#E2E8F0',
    glass: 'rgba(255, 255, 255, 0.08)',
    border: 'rgba(255, 255, 255, 0.15)',
};

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@300;400;600&display=swap');

  body { 
    margin: 0;
    font-family: 'Inter', sans-serif; 
    overflow-x: hidden;
  }

  .main-bg {
    background: 
      radial-gradient(circle at 50% 50%, ${C.bgLight} 0%, transparent 70%),
      linear-gradient(180deg, ${C.bgDark} 0%, ${C.bgMid} 45%, ${C.bgMid} 55%, ${C.bgDark} 100%);
    background-attachment: fixed;
    min-height: 100vh;
  }
`;

const Insights = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(7); // Default 1W
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [latestStats, setLatestStats] = useState(null);

    const fetchPortfolioData = async (selectedDays) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PORTFOLIO_HISTORY(selectedDays)}`, {
                headers: { 'accept': 'application/json' }
            });
            if (!response.ok) throw new Error('Network error');
            const rawData = await response.json();

            // Ensure data is sorted by date
            const sortedData = rawData.sort((a, b) => new Date(a.date) - new Date(b.date));

            // Fill missing dates
            const today = new Date();
            // Ensure we truncate time
            today.setHours(0, 0, 0, 0);

            const filledData = [];
            for (let i = selectedDays - 1; i >= 0; i--) {
                const d = new Date(today);
                d.setDate(d.getDate() - i);

                // Pad to ensure correct local timezone date string
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;

                const existing = sortedData.find(item => item.date === dateString);

                if (existing) {
                    const dateObj = new Date(existing.date);
                    const displayDate = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                    const dayName = dateObj.toLocaleDateString('en-GB', { weekday: 'short' });
                    filledData.push({
                        ...existing,
                        displayDate: displayDate,
                        displayDateWithDay: `${dayName}, ${displayDate}`,
                        purchaseL: (existing.total_purchase / 100000).toFixed(2),
                        currentL: (existing.total_current_value / 100000).toFixed(2),
                        profitVal: existing.total_unrealized_gain,
                        isProfit: existing.total_unrealized_gain >= 0
                    });
                } else {
                    const dayName = d.toLocaleDateString('en-GB', { weekday: 'short' });
                    const displayDate = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                    filledData.push({
                        date: dateString,
                        displayDate: displayDate,
                        displayDateWithDay: `${dayName}, ${displayDate}`,
                        total_purchase: 0,
                        total_current_value: 0,
                        total_unrealized_gain: 0,
                        purchaseL: "0.00",
                        currentL: "0.00",
                        profitVal: 0,
                        isProfit: true
                    });
                }
            }

            setData(filledData);

            if (filledData.length > 0) {
                setLatestStats(filledData[filledData.length - 1]);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPortfolioData(days);
    }, [days]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = GLOBAL_STYLES;
        document.head.appendChild(style);
        return () => style.remove();
    }, []);





    // Theme Colors
    const T = {
        card: isDarkMode ? '#0a0a0a' : 'rgba(255, 255, 255, 0.03)',
        textMain: isDarkMode ? '#e5e7eb' : C.white,
        textDim: isDarkMode ? '#6b7280' : 'rgba(255, 255, 255, 0.7)',
        border: isDarkMode ? '#262626' : C.border,
        accent: isDarkMode ? '#2563eb' : C.accent,
        success: isDarkMode ? '#16a34a' : C.success,
        danger: isDarkMode ? '#dc2626' : C.danger,
        tooltipBg: isDarkMode ? '#111111' : C.bgDark,
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const d = payload[0].payload;
            return (
                <div style={{ backgroundColor: T.tooltipBg, border: `1px solid ${T.border}`, padding: '16px', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                    <p style={{ margin: '0 0 12px', fontWeight: 'bold', color: T.textMain, borderBottom: `1px solid ${T.border}`, paddingBottom: '8px' }}>{d.displayDateWithDay || label}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '8px' }}>
                        <span style={{ color: T.textDim }}>Purchase:</span>
                        <span style={{ color: T.textMain, fontWeight: 600 }}>₹{d.purchaseL}L</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '8px' }}>
                        <span style={{ color: T.textDim }}>Current Value:</span>
                        <span style={{ color: T.textMain, fontWeight: 600 }}>₹{d.currentL}L</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginTop: '12px', paddingTop: '8px', borderTop: `1px solid ${T.border}` }}>
                        <span style={{ color: T.textDim }}>P&L:</span>
                        <span style={{ color: d.isProfit ? T.success : T.danger, fontWeight: 'bold' }}>
                            {d.isProfit ? '+' : '-'}₹{Math.abs(d.total_unrealized_gain / 100000).toFixed(2)}L
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    const CustomBar = ({ x, y, width, height, fill, payload }) => {
        const color = payload?.isProfit ? T.success : T.danger;
        return (
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={color}
                radius={8}
                rx={8}
                ry={8}
            />
        );
    };

    return (
        <div className={!isDarkMode ? "main-bg" : ""} style={{ backgroundColor: isDarkMode ? '#000000' : 'transparent', color: T.textMain, minHeight: '100vh', display: 'flex', flexDirection: 'column', transition: 'background-color 0.3s ease' }}>
            <Header />

            <main style={{ flex: 1, padding: '120px 24px 40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                {/* Trading View Header */}
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '30px', gap: '20px' }}>
                    <div>
                        <h1 style={{ fontSize: '2rem', fontWeight: 800, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Activity size={32} color={T.accent} />
                            Portfolio Momentum
                        </h1>
                        <p style={{ margin: 0, color: T.textDim, fontSize: '1.1rem' }}>Advanced tracking of KKB deployed assets</p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ display: 'flex', background: T.card, border: `1px solid ${T.border}`, borderRadius: '8px', padding: '4px' }}>
                            {[
                                { label: 'WEEK', val: 7 },
                                { label: 'MONTH', val: 30 },
                                { label: 'QUARTER', val: 90 },
                                { label: 'YEAR', val: 365 }
                            ].map(tf => (
                                <button
                                    key={tf.label}
                                    onClick={() => setDays(tf.val)}
                                    style={{
                                        background: days === tf.val ? T.accent : 'transparent',
                                        color: days === tf.val ? '#fff' : T.textDim,
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {tf.label}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => setIsDarkMode(!isDarkMode)}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '44px', height: '44px', borderRadius: '8px',
                                background: T.card, border: `1px solid ${T.border}`,
                                color: T.textMain, cursor: 'pointer', transition: 'all 0.2s ease'
                            }}
                            title={`Switch to ${isDarkMode ? 'Light' : 'Dark'} Mode`}
                        >
                            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>

                {/* Stats Row */}
                {latestStats && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ background: T.card, border: `1px solid ${T.border}`, padding: '24px', borderRadius: '12px' }}>
                            <span style={{ color: T.textDim, fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase' }}>Current Value</span>
                            <div style={{ fontSize: '2rem', fontWeight: 800, marginTop: '8px', color: T.textMain }}>
                                ₹{latestStats.currentL} <span style={{ fontSize: '1rem', color: T.textDim, fontWeight: 500 }}>Lakhs</span>
                            </div>
                        </div>

                        <div style={{ background: T.card, border: `1px solid ${T.border}`, padding: '24px', borderRadius: '12px' }}>
                            <span style={{ color: T.textDim, fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase' }}>Invested Capital</span>
                            <div style={{ fontSize: '2rem', fontWeight: 800, marginTop: '8px', color: T.textMain }}>
                                ₹{latestStats.purchaseL} <span style={{ fontSize: '1rem', color: T.textDim, fontWeight: 500 }}>Lakhs</span>
                            </div>
                        </div>

                        <div style={{ background: T.card, border: `1px solid ${T.border}`, padding: '24px', borderRadius: '12px' }}>
                            <span style={{ color: T.textDim, fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase' }}>Total Unrealized P&L</span>
                            <div style={{ fontSize: '2rem', fontWeight: 800, marginTop: '8px', color: latestStats.isProfit ? T.success : T.danger, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {latestStats.isProfit ? <TrendingUp size={28} /> : <BarChart2 size={28} style={{ transform: 'scaleY(-1)' }} />}
                                {latestStats.isProfit ? '+' : '-'}₹{Math.abs(latestStats.total_unrealized_gain / 100000).toFixed(2)}
                                <span style={{ fontSize: '1rem', opacity: 0.8, fontWeight: 500 }}>Lakhs</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Chart Area */}
                <div style={{
                    background: T.card, border: `1px solid ${T.border}`, borderRadius: '16px',
                    padding: '24px', height: '600px', width: '100%', boxSizing: 'border-box',
                    position: 'relative'
                }}>
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.1)', zIndex: 10, borderRadius: '16px', backdropFilter: 'blur(2px)' }}>
                            <div style={{ color: T.accent, fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Activity size={24} className="animate-spin" /> Loading Market Data...
                            </div>
                        </div>
                    )}

                    {error && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                            <div style={{ color: T.danger, fontWeight: 'bold', fontSize: '1.2rem' }}>Error loading data: {error}</div>
                        </div>
                    )}

                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorCurrentFill" x1="0" y1="0" x2="1" y2="0">
                                    {data.map((d, index) => (
                                        <stop key={`fill-${index}`} offset={`${(index / (data.length - 1 || 1)) * 100}%`} stopColor={d.isProfit ? T.success : T.accent} stopOpacity={0.25} />
                                    ))}
                                </linearGradient>
                                <linearGradient id="colorCurrentStroke" x1="0" y1="0" x2="1" y2="0">
                                    {data.map((d, index) => (
                                        <stop key={`stroke-${index}`} offset={`${(index / (data.length - 1 || 1)) * 100}%`} stopColor={d.isProfit ? T.success : T.accent} stopOpacity={1} />
                                    ))}
                                </linearGradient>
                                <linearGradient id="colorPurchase" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={T.textDim} stopOpacity={0.2} />
                                    <stop offset="95%" stopColor={T.textDim} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis
                                dataKey="displayDate"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: T.textDim, fontSize: 12 }}
                                dy={10}
                                minTickGap={20}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: T.textDim, fontSize: 12 }}
                                tickFormatter={(v) => `₹${v}L`}
                                dx={-10}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: T.border, strokeWidth: 1, strokeDasharray: '5 5' }} />
                            <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '20px' }} />

                            <Area
                                type="monotone"
                                dataKey="purchaseL"
                                name="Invested Capital"
                                stroke={T.textDim}
                                fillOpacity={1}
                                fill="url(#colorPurchase)"
                                strokeWidth={2}
                            />
                            <Area
                                type="monotone"
                                dataKey="currentL"
                                name="Current Value"
                                stroke="url(#colorCurrentStroke)"
                                fillOpacity={1}
                                fill="url(#colorCurrentFill)"
                                strokeWidth={3}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>

                {/* Bar Chart - Comparison */}
                <div style={{
                    background: T.card, border: `1px solid ${T.border}`, borderRadius: '16px',
                    padding: '24px', height: '400px', width: '100%', boxSizing: 'border-box',
                    position: 'relative', marginTop: '30px'
                }}>
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.1)', zIndex: 10, borderRadius: '16px', backdropFilter: 'blur(2px)' }}>
                            <div style={{ color: T.accent, fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Activity size={24} className="animate-spin" /> Loading Market Data...
                            </div>
                        </div>
                    )}

                    {error && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                            <div style={{ color: T.danger, fontWeight: 'bold', fontSize: '1.2rem' }}>Error loading data: {error}</div>
                        </div>
                    )}

                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorInvestedBar" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={T.textDim} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={T.textDim} stopOpacity={0.3} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis
                                dataKey="displayDate"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: T.textDim, fontSize: 12 }}
                                dy={10}
                                minTickGap={20}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: T.textDim, fontSize: 12 }}
                                tickFormatter={(v) => `₹${v}L`}
                                dx={-10}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: T.glass }} />
                            <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '20px' }} />

                            <Bar
                                dataKey="purchaseL"
                                name="Invested Capital"
                                fill="url(#colorInvestedBar)"
                                radius={[8, 8, 0, 0]}
                            />
                            <Bar
                                dataKey="currentL"
                                name="Current Value"
                                shape={<CustomBar />}
                                radius={[8, 8, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Insights;
